"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Mic,
  Sparkles,
  Stethoscope,
  Heart,
  Lock,
  Crown,
  Brain,
  Building2,
  Trophy,
  Briefcase,
  GraduationCap,
  ArrowRight,
  Users,
  Globe2,
  Compass,
  Layers,
  Clock,
  Calendar,
  PlayCircle,
  MessageSquare,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { TreeBlock } from "@/lib/blocks/tree-block";
import { CardGridBlock } from "@/lib/blocks/card-grid-block";
import { AccordionBlock } from "@/lib/blocks/accordion-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
};

const SIGNATURE_TOPICS = [
  {
    num: "01",
    title: "How Excellence Actually Runs",
    icon: Brain,
    body: "Why execution beats vision, and what an operating system for excellence contains. Most organizations confuse aspiration with operation. This keynote dismantles that confusion with the precision of a clinical diagnosis, revealing the protocols, checklists, decision frameworks, and accountability structures that separate organizations that achieve excellence from organizations that merely aspire to it.",
    ideal: "Executives, operations leaders, management conferences, corporate retreats, healthcare administration forums, government operations summits.",
    duration: "45–60 minutes · Workshop extension 2–3 hours",
    accent: "red",
  },
  {
    num: "02",
    title: "Leadership as Diagnosis",
    icon: Stethoscope,
    body: "What medicine teaches about running organizations. The physician&apos;s method — diagnosis, triage, protocol, judgment, outcome accountability — is not a metaphor for leadership. It IS leadership, applied with clinical rigor. Six clinical principles are presented, each demonstrated through the parallel between clinical practice and organizational operation.",
    ideal: "Leadership development programs, executive education, healthcare leadership conferences, women&apos;s leadership summits, medical school commencements.",
    duration: "40–55 minutes · Extended workshop 2–4 hours",
    accent: "purple",
  },
  {
    num: "03",
    title: "The 70% Project",
    icon: Heart,
    body: "The operating case for the talent the world overlooks. A decade of operational data proving that teams composed of over 70% women produce the most reliable outcomes. The audience learns the five operating advantages observed in majority-female teams: communication, risk discipline, collaboration, retention, and ego management — and why diversity programs that argue from operations succeed where those that argue from morality fail.",
    ideal: "Diversity and inclusion conferences, women&apos;s leadership summits, HR leadership forums, corporate diversity programs, executive retreats.",
    duration: "45–60 minutes · Workshop extension 2–3 hours",
    accent: "emerald",
  },
  {
    num: "04",
    title: "Build in the Dark",
    icon: Lock,
    body: "The operator&apos;s discipline in an attention economy. While the world chases visibility, the most consequential operations run in silence. This keynote draws from the discipline that has produced zero security incidents across fifteen-plus years of sovereign-level engagements: compartmentalized operations, need-to-know access, operational security as religion. The audience learns why discretion is a competitive advantage.",
    ideal: "Executive leadership programs, security conferences, government leadership summits, corporate boards, family office gatherings.",
    duration: "40–55 minutes · Workshop extension 2–3 hours",
    accent: "denim",
  },
  {
    num: "05",
    title: "The Triple Threat",
    icon: Layers,
    body: "Medicine, technology, and politics as one operating method. Three domains that conventional wisdom treats as separate, integrated into a single operating method that produces outcomes single-domain experts cannot achieve. The Triple Threat is not three skills. It is one method applied across three domains — the clinical precision of a physician, the systems architecture of a technologist, the strategic discipline of a political operator.",
    ideal: "Cross-disciplinary conferences, executive MBA programs, public policy summits, technology leadership forums, healthcare innovation events.",
    duration: "45–60 minutes · Workshop extension 2–3 hours",
    accent: "cyan",
  },
  {
    num: "06",
    title: "Women in the C-Suite",
    icon: Crown,
    body: "Lessons from a decade of operating at sovereign scale as a woman. Not a motivational talk. An operational briefing on what it takes to lead at sovereign scale as a woman — the discipline, the methodology, the evidence, and the results. Drawing from experience assembling teams of over 70% women across eighteen countries, this topic delivers the operational reality of women in leadership, grounded in verified outcomes.",
    ideal: "Women&apos;s leadership summits, corporate women-in-leadership programs, executive women&apos;s networks, governance reform conferences, professional association events.",
    duration: "40–55 minutes · Workshop extension 2–3 hours",
    accent: "amber-gold",
  },
];

const FORMATS = [
  { icon: PlayCircle, title: "Keynote Address", body: "35–65 minutes of concentrated operational insight. The primary delivery mode — structured, evidence-based, and calibrated to the audience and the occasion.", accent: "red" },
  { icon: MessageSquare, title: "Fireside Chat", body: "30–45 minutes of conversational depth, ideal for intimate gatherings and moderated discussions. The format allows for nuanced exploration of the operating method.", accent: "purple" },
  { icon: Users, title: "Panel Participation", body: "Multi-participant discussions where Dr. Kush contributes the cross-domain perspective that single-discipline panels often lack — medicine, technology, and politics as one method.", accent: "cyan" },
  { icon: Globe2, title: "Virtual & Remote", body: "Full operational standards maintained through digital delivery. The same authority, evidence, and outcome accountability as in-person engagements, optimized for distributed audiences.", accent: "emerald" },
  { icon: Clock, title: "Extended Q&A", body: "Deep engagement sessions where audiences interrogate the operating method directly. The format produces the most actionable takeaways — protocols, checklists, and decision frameworks clarified in dialogue.", accent: "amber-gold" },
  { icon: Calendar, title: "Multi-Day Programs", body: "Immersive keynote-to-workshop sequences. The keynote introduces the operating method; subsequent sessions implement it within the audience&apos;s organizational context.", accent: "denim" },
];

const AUDIENCES = [
  { icon: Building2, title: "Conference Organizers", body: "Leadership conferences, industry summits, and global forums seeking a speaker with genuine operational authority.", accent: "red" },
  { icon: Briefcase, title: "Corporate Event Planners", body: "Executive retreats, leadership offsites, and corporate events requiring substance over spectacle.", accent: "purple" },
  { icon: GraduationCap, title: "University Programs", body: "Business schools, medical schools, and executive education programs seeking cross-domain expertise.", accent: "cyan" },
  { icon: Crown, title: "Government Events", body: "Public sector leadership forums, civil service programs, and governance reform conferences.", accent: "emerald" },
  { icon: Trophy, title: "Women&apos;s Leadership Summits", body: "Women-in-leadership conferences and women&apos;s professional networks requiring operational evidence and methodology.", accent: "amber-gold" },
  { icon: Lock, title: "Private Engagement Hosts", body: "Discreet, invitation-only gatherings for sovereign-level clients and high-stakes audiences.", accent: "denim" },
];

const PRINCIPLES = [
  { num: "01", title: "Evidence Over Inspiration", body: "Every insight extracted from verified operational experience — the kind that has been tested across sovereign-scale deployments where the margin for error is zero.", accent: "red" },
  { num: "02", title: "Systems Over Stories", body: "Frameworks are not elegant summaries. They are operating mechanisms — protocols, checklists, decision frameworks — that attendees deploy the following morning.", accent: "purple" },
  { num: "03", title: "Calibration Over Genericization", body: "Each keynote is a bespoke operational briefing tailored to the audience, the occasion, and the operational objectives — not a recycled speech across events.", accent: "cyan" },
  { num: "04", title: "Implementation Over Applause", body: "Success is measured by whether the operating method was adopted and whether outcomes improved — not by audience satisfaction surveys or social media engagement.", accent: "emerald" },
];

export default function KeynoteSpeakingPage() {
  return (
    <>
      <PageHero
        icon={Mic}
        overline="Service · Keynote Speaking"
        overlineClassName="text-red"
        title={
          <>
            Operational Authority, <span className="text-gradient-premium">Delivered from the Stage.</span>
          </>
        }
        subtitle="Dr. Jyoti Kush delivers keynotes that are not motivational performances. They are operational briefings drawn from a decade of building and running sovereign-scale operations across eighteen countries — the kind of authority that transforms how organizations think about execution, governance, and the talent the world overlooks."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Services", href: "/services/" }, { label: "Keynote Speaking" }]}
        badge="Six signature topics. One operating method."
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-5`}>
              <Sparkles className="size-3.5 text-red" />
              <span className="text-overline text-red">The Thesis</span>
            </div>
            <h2 className="text-display-md mb-5">
              Most keynote speakers theorize. Dr. Jyoti Kush operationalizes.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              Every insight is drawn from verified operational experience — the kind that shapes nations, not just quarterly targets. The medical background adds a dimension no other speaker at this level brings: the clinical method as an actual operating system, not a metaphor. Where other speakers offer inspiration, the operating method offers implementation — the protocols, checklists, and decision frameworks that attendees can deploy the following morning.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              Six signature topics. Each rooted in operational reality. Each delivered with the clinical precision and commanding authority that define the operating method. From operational excellence to women&apos;s empowerment, from the intersection of medicine and technology to the discipline of building in discretion — the keynote portfolio spans the full spectrum of sovereign-scale leadership.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The preparation behind every keynote mirrors the preparation behind every sovereign-level engagement. Before delivering, the same diagnostic phase is applied — understanding the audience, their challenges, their organizational context, and their desired outcomes. The keynote is then calibrated, not genericized, to address the specific realities of the room.
            </p>
          </div>

          <div className="space-y-3">
            {PRINCIPLES.map((p, i) => {
              const a = ACCENT[p.accent];
              return (
                <motion.div
                  key={p.num}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`size-11 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0 ring-2 ${a.ring}`}>
                      <span className="text-caption font-bold text-white">{p.num}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{p.title}</h3>
                      <p className="text-body-sm text-muted-foreground leading-relaxed">{p.body}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Brain className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The Six Signature Topics</span>
          </div>
          <h2 className="text-display-md mb-4">Topics grounded in operational reality.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each topic addresses a distinct dimension of operational leadership. All drawn from verified experience, tailored to the audience, and designed to produce implementable takeaways.
          </p>
        </div>
        <div className="space-y-4">
          {SIGNATURE_TOPICS.map((t, i) => {
            const a = ACCENT[t.accent];
            return (
              <motion.div
                key={t.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-6 lg:p-7 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="grid lg:grid-cols-[auto_1fr] gap-6">
                  <div className="flex items-start gap-4 lg:flex-col lg:items-center">
                    <div className={`size-14 rounded-2xl bg-gradient-sovereign flex items-center justify-center shadow-xl ring-2 ${a.ring} group-hover:scale-110 group-hover:rotate-3 transition-all`}>
                      <t.icon className="size-7 text-white" />
                    </div>
                    <div className={`text-display-sm font-bold ${a.text} opacity-30 lg:opacity-100`}>{t.num}</div>
                  </div>
                  <div>
                    <h3 className={`text-display-sm mb-3 ${a.text}`}>{t.title}</h3>
                    <p className="text-body-md text-muted-foreground leading-relaxed mb-4">{t.body}</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className={`text-caption ${a.text} font-semibold`}>
                        <span className="text-overline opacity-70 block mb-1">Ideal Audiences</span>
                        {t.ideal}
                      </div>
                      <div className={`text-caption ${a.text} font-semibold`}>
                        <span className="text-overline opacity-70 block mb-1">Duration</span>
                        {t.duration}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <PlayCircle className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Formats</span>
          </div>
          <h2 className="text-display-md mb-4">Flexibility for every event requirement.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each format is adapted to the audience, the occasion, and the operational objectives of the event. Every format maintains the same operational standards — informed perspective grounded in verified experience, timely delivery, and absolute discretion.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FORMATS.map((f, i) => {
            const a = ACCENT[f.accent];
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center mb-4 shadow-lg`}>
                  <f.icon className="size-6 text-white" />
                </div>
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{f.title}</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed">{f.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Trophy className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Ideal Clients</span>
          </div>
          <h2 className="text-display-md mb-4">Who books the keynote.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Organizations and event organizers seeking a speaker who brings genuine operational authority rather than polished theory — those who recognize that the stakes they face demand substance over spectacle.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {AUDIENCES.map((a, i) => {
            const ac = ACCENT[a.accent];
            return (
              <motion.div
                key={a.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${ac.bg} to-transparent border ${ac.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <a.icon className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-1.5 ${ac.text}`}>{a.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{a.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Sparkles className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Speaking Topic Tree</span>
          </div>
          <h2 className="text-display-md mb-4">The operating method, six topics, twenty sub-themes.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each signature topic branches into sub-themes that allow the keynote to be calibrated to the specific operational challenges of the audience. The tree structure preserves the operating method while enabling precise application.
          </p>
        </div>
        <TreeBlock
          entries={[
            {
              id: "k1",
              label: "How Excellence Actually Runs",
              body: "The operating system for execution: protocols, checklists, decision frameworks, accountability. The method that drives 99.9999% uptime.",
              accent: "red",
              children: [
                { id: "k1a", label: "Protocols Over Inspiration", body: "Why operational systems outperform values statements and how culture follows structure rather than the reverse." },
                { id: "k1b", label: "Decision Frameworks Under Pressure", body: "Replacing instinct with evidence-based decision systems that produce reliable outcomes when stakes are highest." },
                { id: "k1c", label: "Accountability Architecture", body: "The structural mechanisms that ensure execution translates into results — not just activity into output." },
              ],
            },
            {
              id: "k2",
              label: "Leadership as Diagnosis",
              body: "The physician's method as an operating system: diagnosis, triage, protocol, judgment, outcome accountability.",
              accent: "purple",
              children: [
                { id: "k2a", label: "Diagnose Before Treating", body: "Why the clinical method's discipline of examination before intervention produces better organizational outcomes than intuitive action." },
                { id: "k2b", label: "Triage Under Pressure", body: "How emergency medicine's prioritization discipline translates to leadership when multiple demands compete for attention and resources." },
                { id: "k2c", label: "Own the Outcome", body: "The accountability standard that distinguishes a physician from a technician — and a leader from a manager." },
              ],
            },
            {
              id: "k3",
              label: "The 70% Project",
              body: "A decade of operational data proving that teams over 70% women produce the most reliable outcomes.",
              accent: "emerald",
              children: [
                { id: "k3a", label: "The Five Operating Advantages", body: "Communication, risk discipline, collaboration, retention, ego management — the measurable dimensions where majority-female teams outperform." },
                { id: "k3b", label: "Diversity From Operations", body: "Why programs that argue from operational evidence succeed where programs that argue from morality fail." },
              ],
            },
            {
              id: "k4",
              label: "Build in the Dark",
              body: "The operator's discipline in an attention economy — discretion as a competitive advantage, not a constraint.",
              accent: "denim",
              children: [
                { id: "k4a", label: "Compartmentalized Operations", body: "How need-to-know architecture and platform-enforced isolation produce zero security incidents across sovereign-scale deployments." },
                { id: "k4b", label: "Duration Over Disruption", body: "Why long-term operational discipline outperforms the constant visibility cycle — and how to build for the former." },
              ],
            },
            {
              id: "k5",
              label: "The Triple Threat",
              body: "Medicine, technology, and politics as one operating method — integration where conventional wisdom keeps them separate.",
              accent: "cyan",
              children: [
                { id: "k5a", label: "Cross-Domain Integration", body: "How the intersection of clinical precision, systems architecture, and strategic discipline produces outcomes single-domain experts cannot match." },
                { id: "k5b", label: "AI Governance Through Triple Lens", body: "Why the most demanding technology problems of the next decade require medical, technological, and political perspectives applied simultaneously." },
              ],
            },
            {
              id: "k6",
              label: "Women in the C-Suite",
              body: "Lessons from a decade of operating at sovereign scale as a woman — operational briefing, not motivational talk.",
              accent: "amber-gold",
              children: [
                { id: "k6a", label: "Operational Discipline Over Charisma", body: "Why systems outperform heroics — and what that means for women leaders navigating environments built for the latter." },
                { id: "k6b", label: "Results Over Visibility", body: "The leadership principle that sovereign-scale operations validate: outcomes are the only metric that compounds." },
              ],
            },
          ]}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Trophy className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Stages of Record</span>
          </div>
          <h2 className="text-display-md mb-4">Past stages and forums.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The keynote speaking service has been present at forums where the stakes are real and the audience demands substance. The caliber of the stage reflects the caliber of the work.
          </p>
        </div>
        <CardGridBlock
          variant="tag"
          columns={3}
          items={[
            { title: "The Nobel Peace Prize", body: "Among the most prestigious gatherings in the world, where leaders convene to address the most consequential challenges facing humanity.", tag: "Global", accent: "red" },
            { title: "The World Economic Forum", body: "Where global leaders from government, business, and civil society convene to shape international agendas and policy direction.", tag: "Global", accent: "purple" },
            { title: "Government Engagements", body: "Speaking to decision-makers operating at the highest levels of consequence, including presidential offices, prime ministerial offices, and defense agencies.", tag: "Sovereign", accent: "cyan" },
            { title: "Corporate Leadership Events", body: "Fortune-scale organizations seeking operational authority for their executive audiences and leadership team offsites.", tag: "Enterprise", accent: "emerald" },
            { title: "University & Academic Forums", body: "Sharing the operating method with the next generation of leaders at business schools, medical schools, and executive education programs.", tag: "Academic", accent: "amber-gold" },
            { title: "Women's Leadership Summits", body: "Presenting the operational case for women in leadership to audiences committed to building high-performance diverse teams.", tag: "Summit", accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <MessageSquare className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Frequently Asked</span>
          </div>
          <h2 className="text-display-md mb-4">Practical questions about the keynote service.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Inquiries frequently address booking, customization, formats, and post-event pathways. The diagnostic conversation covers everything the FAQ cannot.
          </p>
        </div>
        <AccordionBlock
          items={[
            { q: "How far in advance should the keynote be booked?", a: "Early booking is recommended for major conferences, flagship corporate events, and international forums. The preparation phase typically requires four to six weeks for optimal customization, though shorter timelines can be accommodated for urgent requirements. Time-sensitive engagements are evaluated individually." },
            { q: "What is the typical audience size for the keynotes?", a: "Keynotes are delivered to audiences ranging from intimate executive gatherings of twenty to conference audiences of several thousand. The format adapts: fireside chats and executive briefings for smaller groups, keynotes and panels for larger audiences. Each format maintains the same operational standards regardless of size." },
            { q: "Can a keynote be customized for a specific industry?", a: "Yes. Every keynote is customized to the audience's domain, their specific challenges, and the outcomes they seek. The operating method is universal; the application is precise. The customization process involves research into the audience context and development of industry-specific examples, frameworks, and takeaways." },
            { q: "Does the service offer post-event follow-up?", a: "Yes. Post-event resources, frameworks, implementation guides, and follow-up sessions are available. The follow-up ensures the keynote produces lasting operational change rather than temporary inspiration. Organizations frequently transition to executive advisory, corporate workshops, or operational consulting as a natural progression." },
            { q: "Are virtual and remote formats available?", a: "Yes. All speaking formats are available for virtual and remote events. Virtual delivery is optimized for distributed audiences, with interactive elements that maintain engagement across digital channels. A pre-event tech rehearsal is required for virtual engagements to verify connectivity, audio quality, and platform functionality." },
            { q: "What makes the speaking different from other keynote speakers?", a: "Three structural differences. First, the medical method — a physician's discipline applied to organizational leadership, producing diagnostic precision that conventional speaking cannot match. Second, a decade of building, not consulting — sovereign-scale operational reality, not synthesized frameworks. Third, outcome accountability — engagements measured by verified results, not audience surveys or social media engagement." },
            { q: "How does the booking process work?", a: "The process begins with an inquiry specifying the event, audience, topic preference, date, and format. A diagnostic conversation follows to understand event objectives and desired outcomes. The keynote is then customized to the audience. Delivery is followed by optional post-event support. The entire process mirrors the clinical method: understand before prescribing." },
            { q: "What technical requirements does the keynote need?", a: "Standard professional AV and stage infrastructure. Professional lapel or headset microphone preferred. Optional projection capability for supporting slides. Stage setup with minimum ten feet depth for natural movement. For virtual: high-speed internet, professional webcam 1080p minimum, dedicated audio input, and a quiet well-lit environment." },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-red/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-purple/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Mic className="size-10 text-red mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Book a keynote.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Every keynote engagement begins with a diagnostic conversation — the clinical method applied to the event itself. Inquiries are accepted through the contact channels, specifying the event, audience, topic preference, date, and format.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Inquire About Booking
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/services/executive-advisory/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Briefcase className="size-4" />
                Executive Advisory
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "University Lectures", description: "Academic lectures and guest sessions for business schools and executive education programs.", href: "/services/university-lectures/", icon: GraduationCap, accent: "coral" },
          { title: "Executive Advisory", description: "The operating method embedded in leadership teams and daily practice.", href: "/services/executive-advisory/", icon: Briefcase, accent: "purple" },
          { title: "Corporate Workshops", description: "Intensive team training on the operating method — systems, not slide decks.", href: "/services/corporate-workshops/", icon: Layers, accent: "lime" },
          { title: "All Services", description: "Ten engagement types across six service modalities. One operating method.", href: "/services/", icon: Compass, accent: "red" },
        ]}
      />
    </>
  );
}
